import { IQuoteLineItem } from "./types";
import { EditorSectionDates } from "../components/QuoteSectionBar/sectionUtils";

// v7 (draft-recovery rework): drafts are now one per editing session (`draftId`), not one per
// quote type - a new quote gets a generated id, an in-progress edit of an existing quote uses that
// quote's own id, so multiple drafts of the same type (two tabs, a new quote alongside an existing
// edit) coexist instead of overwriting one shared slot.
// `version` is written for diagnostics only - a stored draft is no longer discarded outright just
// because its version doesn't match. See applyQuoteDraft.ts, which rebuilds from a known-good
// baseline (a fresh new quote, or the freshly loaded existing quote) and defensively overlays
// whatever fields of a stored draft - any version, any shape - still validate, rather than an
// all-or-nothing read. A future field rename/removal degrades to a partial restore with a visible
// warning instead of silently discarding real in-progress work.
export const QUOTE_DRAFT_VERSION = 7;
const prefix = "quick_quote_draft";

// A draft this old is treated as gone - an abandoned quote shouldn't linger in localStorage forever
// just because nobody ever opened the Drafts dialog to clear it.
const MAX_DRAFT_AGE_MS = 90 * 24 * 60 * 60 * 1000;
// A write must always succeed for the quote actively being edited right now - if storage is full,
// the single oldest OTHER local draft is evicted and the write retried, repeating up to this many
// times rather than looping forever. Not a real-world limit; localStorage would run out of
// meaningfully-sized drafts to evict long before this bound matters.
const MAX_EVICTIONS_PER_WRITE = 50;

export type QuoteDraftSection = { name: string; dates?: EditorSectionDates; items: IQuoteLineItem[] };

export type QuoteDraft = {
  version: number;
  draftId: string;
  // null = a new, not-yet-created quote; set = an in-progress edit of this existing quote.
  quoteId: string | null;
  quoteType: "customer" | "prospect";
  customerGuid: string | null;
  prospectName: string;
  name: string;
  description: string;
  // QuoteDetailPage only - CreateQuotePage has no internal note field, so it never writes this.
  internalNote?: string;
  hasAffiliate: boolean;
  affiliateName: string;
  discounts: { monthly: string; installment: string; annual: string };
  sections: QuoteDraftSection[];
  updatedAt: string;
};

const userKey = () => (sessionStorage.getItem("email") || "anonymous").trim().toLowerCase();
export const quoteDraftKey = (quoteType: QuoteDraft["quoteType"], draftId: string) => `${prefix}:${userKey()}:${quoteType}:${draftId}`;

let draftIdSeq = 0;
export const newDraftId = () => `draft-${Date.now().toString(36)}-${draftIdSeq++}`;

// A missing/malformed updatedAt is never treated as expired - we only act on a positively-confirmed
// age, not a guess.
const isExpired = (updatedAt: unknown, now: number) => {
  if (typeof updatedAt !== "string") return false;
  const at = new Date(updatedAt).getTime();
  return Number.isFinite(at) && now - at > MAX_DRAFT_AGE_MS;
};

// Every localStorage key belonging to this user's drafts (any quote type, any draftId).
const ownKeys = (): string[] => {
  const ownPrefix = `${prefix}:${userKey()}:`;
  const keys: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key && key.startsWith(ownPrefix)) keys.push(key);
  }
  return keys;
};

// Only genuinely unreadable storage (bad JSON, not an object at all) or an expired draft is
// discarded here. A draft whose fields have otherwise drifted from the current shape is still
// returned as-is - applyQuoteDraft is what decides, field by field, what can actually be restored.
export const readQuoteDraft = (quoteType: QuoteDraft["quoteType"], draftId: string): unknown | null => {
  const key = quoteDraftKey(quoteType, draftId);
  const raw = localStorage.getItem(key);
  if (raw == null) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) { localStorage.removeItem(key); return null; }
    if (isExpired((parsed as Record<string, unknown>).updatedAt, Date.now())) { localStorage.removeItem(key); return null; }
    return parsed;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

// Removes this user's single oldest draft (by its own updatedAt) other than `excludeKey` - the key
// currently being written, since overwriting itself never frees room for a larger payload. Returns
// whether anything was actually evicted.
const evictOldest = (excludeKey: string): boolean => {
  let oldestKey: string | null = null;
  let oldestAt = Infinity;
  for (const key of ownKeys()) {
    if (key === excludeKey) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || "null");
      const updatedAt = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>).updatedAt : null;
      const at = typeof updatedAt === "string" ? new Date(updatedAt).getTime() : 0; // unreadable/undated - evict it first
      if (at < oldestAt) { oldestAt = at; oldestKey = key; }
    } catch {
      oldestKey = key; // unparseable entry - as good a candidate to clear as any
      break;
    }
  }
  if (!oldestKey) return false;
  localStorage.removeItem(oldestKey);
  return true;
};

// Never fails to save the quote actively being edited right now. Runs inside a debounced setTimeout
// on every editor page, so an uncaught exception there wouldn't crash the app - but silently losing
// the CURRENT autosave to a full quota would be worse than evicting some older, already-abandoned
// draft to make room, so a storage-full write evicts the oldest other draft and retries rather than
// giving up immediately. Only gives up (silently - the in-progress edit is still safe in React
// state either way) once there is genuinely nothing left to evict.
export const writeQuoteDraft = (draft: Omit<QuoteDraft, "version" | "updatedAt">) => {
  const key = quoteDraftKey(draft.quoteType, draft.draftId);
  const payload = JSON.stringify({ ...draft, version: QUOTE_DRAFT_VERSION, updatedAt: new Date().toISOString() });
  for (let attempt = 0; attempt <= MAX_EVICTIONS_PER_WRITE; attempt++) {
    try {
      localStorage.setItem(key, payload);
      return;
    } catch {
      if (!evictOldest(key)) return;
    }
  }
};

export const clearQuoteDraft = (quoteType: QuoteDraft["quoteType"], draftId: string) =>
  localStorage.removeItem(quoteDraftKey(quoteType, draftId));

// A lightweight, defensively-read summary for listing every local draft - just enough to label a
// row (who/what it's for, when it was last autosaved) without the full apply-time validation
// applyQuoteDraft does when a specific draft is actually restored.
export type QuoteDraftSummary = {
  draftId: string;
  quoteType: QuoteDraft["quoteType"];
  quoteId: string | null;
  customerGuid: string | null;
  prospectName: string;
  name: string;
  updatedAt: string;
};

const KEY_PATTERN = /^quick_quote_draft:[^:]*:(customer|prospect):(.+)$/;

const summarize = (key: string, raw: unknown): QuoteDraftSummary | null => {
  const match = KEY_PATTERN.exec(key);
  if (!match || typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  const updatedAt = typeof value.updatedAt === "string" ? value.updatedAt : "";
  if (!updatedAt) return null;
  return {
    draftId: match[2],
    quoteType: match[1] as QuoteDraft["quoteType"],
    quoteId: typeof value.quoteId === "string" ? value.quoteId : null,
    customerGuid: typeof value.customerGuid === "string" ? value.customerGuid : null,
    prospectName: typeof value.prospectName === "string" ? value.prospectName : "",
    name: typeof value.name === "string" ? value.name : "",
    updatedAt,
  };
};

// Also sweeps out anything past MAX_DRAFT_AGE_MS as it scans - the Drafts dialog and QuoteListPage
// both call this, so an expired draft gets garbage-collected the next time either is opened, not
// just left for the (less likely to ever run) eviction path in writeQuoteDraft to eventually find.
export const listQuoteDrafts = (): QuoteDraftSummary[] => {
  const now = Date.now();
  const summaries: QuoteDraftSummary[] = [];
  for (const key of ownKeys()) {
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || "null");
      const updatedAt = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>).updatedAt : undefined;
      if (isExpired(updatedAt, now)) { localStorage.removeItem(key); continue; }
      const summary = summarize(key, parsed);
      if (summary) summaries.push(summary);
    } catch { /* unreadable entry - skip rather than list it */ }
  }
  return summaries.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
};
